// Build identity for the bundle, and the two places it is observable.
//
// Before this existed nothing in a running widget said which build it was, so
// "the customer is on a bad bundle" was unfalsifiable from our side and
// unanswerable from theirs. Two consumers now:
//
//   1. Every API call carries it as the platform SDK-identity pair, which the
//      server's access log already stamps as sdk_name / sdk_version — so
//      bundle version is queryable per tenant without a server change.
//   2. `window.__SPEECHIFY_WIDGET_VERSION__` for support: "open the console
//      and paste this" works on a site we have no other access to.

declare global {
  // eslint-disable-next-line no-var
  var __SPEECHIFY_WIDGET_BUILD__: string;
  // eslint-disable-next-line no-var
  var __SPEECHIFY_WIDGET_VERSION__: string;
}

/** Reported when the bundle was assembled outside build.mjs (vitest, ts-node). */
const DEV_BUILD = "0.0.0-dev";

/** Header names — the platform SDK-identity pair (pkg/httphdr, api-guidelines §11). */
const SDK_NAME_HEADER = "Speechify-SDK";
const SDK_VERSION_HEADER = "Speechify-SDK-Version";

/** Identifies the widget among API clients in the server's access log. */
export const WIDGET_SDK_NAME = "@speechify/agents-widget";

/**
 * The build's `<semver>+<sha>` identity.
 *
 * Sanitised on read rather than trusted: the value reaches an HTTP header, and
 * a header value carrying a control character or a newline is a request
 * -splitting primitive. build.mjs already restricts it, but this is the side
 * that would be exploited, and the global is writable by the host page.
 */
export function widgetVersion(): string {
  const raw = typeof globalThis.__SPEECHIFY_WIDGET_BUILD__ === "string" ? globalThis.__SPEECHIFY_WIDGET_BUILD__ : "";
  const safe = raw.replace(/[^A-Za-z0-9.+_-]/g, "").slice(0, 64);
  return safe || DEV_BUILD;
}

/** SDK-identity headers to merge into every request the widget makes. */
export function versionHeaders(): Record<string, string> {
  return {
    [SDK_NAME_HEADER]: WIDGET_SDK_NAME,
    [SDK_VERSION_HEADER]: widgetVersion(),
  };
}

/**
 * Publish the build identity on the global object so a support engineer can
 * read it off a customer's console. Called once on bundle load.
 */
export function publishWidgetVersion(target: typeof globalThis = globalThis): void {
  try {
    target.__SPEECHIFY_WIDGET_VERSION__ = widgetVersion();
  } catch {
    // A frozen or proxied global on the host page must not stop the element
    // from registering — the version readout is a diagnostic, not a feature.
  }
}
